import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  Collapse,
} from "antd";
import { debounce } from "lodash";
import { Radio, Switch, Table, Checkbox } from "antd";
import {
  DownOutlined,
  InfoCircleOutlined,
  UpOutlined,
} from "@ant-design/icons";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  prefixSelector,
  validateMobileNumber,
} from "../../utils/mobileValidation";
import { useCreateEditUser } from "./hooks/useCreateEditUser";
import { searchTeams } from "../../actionCreator/teamManagementActionCreator";
import { searchRoles } from "../../actionCreator/roleManagementActionCreator";
import { AuthContext } from "../../context/AuthContext";
import { userRoles } from "../../config";
import { getTeamNamesFromUser } from "../../utils/teamAccess";
const { Option } = Select;

const CreateUserForm = ({ initialValues, closeModal }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { user } = useContext(AuthContext);
  const { subscription } = useContext(AuthContext);
  const users = useSelector((state) => state.UserManagementReducer.users);

  const {
    handleCreateUpdateUser,
    error,
    loading,
    setError,
    isTeamChange,
    setTeamChange,
  } = useCreateEditUser();

  const [isDisable, setDisable] = useState(false);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [roleFetching, setRoleFetching] = useState(false);
  const [isLimitExceed, setLimitExceed] = useState(false);

  const [leadAccessScope, setLeadAccessScope] = useState("SPECIFIC_USERS");
  const [leadPermissions, setLeadPermissions] = useState([]);

  const mapApiPermissionsToUI = (apiPermissions = [], usersList = []) =>
    apiPermissions.map((item) => {
      const leadIds = item.permissions?.Leads?.map((p) => p.id) || [];
      const entity = usersList.find((u) => u.id === item.targetUserId) || {};

      return {
        id: item.targetUserId,
        firstName: entity.firstName,
        lastName: entity.lastName,
        teamName: getTeamNamesFromUser(entity).join(", "),
        view: leadIds.includes(1),
        edit: leadIds.includes(2),
        transfer: leadIds.includes(8),
      };
    });

  const buildLeadPermissionPayload = () =>
    leadPermissions
      ?.filter((d) => d?.id !== initialValues?.id)
      ?.filter((i) => i?.id !== user?.id)
      .map((item) => {
        const Leads = [];
        if (item.view) Leads.push({ id: 1 });
        if (item.edit) Leads.push({ id: 2 });
        if (item.transfer) Leads.push({ id: 8 });

        return {
          targetUserId: item.id,
          permissions: { Leads },
        };
      });

  const updatePermission = (id, key, value) => {
    setLeadPermissions((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item)),
    );
  };

  const getPermissionTableData = () => {
    return leadPermissions
      ?.filter((d) => d?.id !== initialValues?.id)
      ?.filter((i) => i?.id !== user?.id);
  };

  useEffect(() => {
    if (!initialValues?.id) return;

    const sourceData = users?.content || [];

    setLeadPermissions((prev) => {
      const existingMap = new Map(prev.map((p) => [p.id, p]));

      return sourceData.map((item) => {
        const existing = existingMap.get(item.id);

        return (
          existing || {
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            teamName: getTeamNamesFromUser(item).join(", "),
            view: initialValues?.userFeaturePermission
              ?.find((d) => d?.targetUserId === item.id)
              ?.permissions?.Leads?.find((i) => i?.name === "View"),
            edit: initialValues?.userFeaturePermission
              ?.find((d) => d?.targetUserId === item.id)
              ?.permissions?.Leads?.find((i) => i?.name === "Edit"),
            transfer: initialValues?.userFeaturePermission
              ?.find((d) => d?.targetUserId === item.id)
              ?.permissions?.Leads?.find((i) => i?.name === "Transfer"),
          }
        );
      });
    });
  }, [leadAccessScope, users, initialValues]);

  const columns = [
    {
      title: "Lead Owner",
      render: (_, record) => (
        <div>
          {record?.firstName} {record?.lastName}{" "}
          {leadAccessScope === "TEAM" &&
            `(${record?.teamName ? record?.teamName : "Unassigned"})`}
        </div>
      ),
    },
    {
      title: "View",
      render: (_, record) => (
        <Checkbox
          checked={record?.view}
          onChange={(e) =>
            updatePermission(record.id, "view", e.target.checked)
          }
        />
      ),
    },
    {
      title: "Edit",
      render: (_, record) => (
        <Checkbox
          checked={record?.edit}
          onChange={(e) =>
            updatePermission(record.id, "edit", e.target.checked)
          }
        />
      ),
    },
    {
      title: "Transfer",
      render: (_, record) => (
        <Checkbox
          checked={record.transfer}
          onChange={(e) =>
            updatePermission(record.id, "transfer", e.target.checked)
          }
        />
      ),
    },
  ];

  useEffect(() => {
    if (initialValues?.id) {
      form.setFieldsValue({
        ...initialValues,
        allowTransferToAdmin: !!initialValues?.allowTransferToAdmin,
        prefix: formatAndExtractCountryCode(initialValues?.mobileNo)
          ?.countryLabel,
        mobileNo: formatAndExtractCountryCode(initialValues?.mobileNo)
          ?.numberWithoutCountryCode,
        fullName: initialValues?.firstName + " " + initialValues?.lastName,
        teams:
          initialValues?.teams ||
          (initialValues?.team ? [initialValues?.team] : []),
      });
      setDisable(true);
    }
  }, [initialValues, form]);

  useEffect(() => {
    if (initialValues?.leadPermissions) {
      setLeadPermissions(
        mapApiPermissionsToUI(
          initialValues.leadPermissions,
          users?.content || [],
        ),
      );
    }
  }, [initialValues, users]);

  useEffect(() => {
    const fetchDefault = async () => {
      const teamRes = await dispatch(searchTeams());
      const roleRes = await dispatch(searchRoles());
      setTeams(teamRes?.content || []);
      setRoles(roleRes?.roles || []);
    };
    fetchDefault();
  }, [dispatch]);

  const fetchTeams = debounce(async (value) => {
    setFetching(true);
    const res = await dispatch(searchTeams(value));
    setTeams(res?.content || []);
    setFetching(false);
  }, 500);

  const fetchRoles = debounce(async (value) => {
    setRoleFetching(true);
    const res = await dispatch(searchRoles(value));
    setRoles(res?.roles || []);
    setRoleFetching(false);
  }, 500);

  return (
    <>
      <div className="tw-bg-slate-50 tw-text-slate-800 tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50 tw-font-inter">
        <div className="tw-text-lg tw-font-bold tw-mb-1">
          {initialValues?.id ? "Edit User Details" : "Create New User"}
        </div>
        {users?.totalElements >=
        subscription?.subscriptionPackage?.userLimit ? (
          <div className="heading12px text-error">
            You’ve already reached the user creation limit. A maximum of{" "}
            {subscription?.subscriptionPackage?.userLimit} users can be created.
          </div>
        ) : (
          <div className="tw-text-xs tw-text-slate-500 tw-font-medium">
            {initialValues?.id
              ? "Edit user settings to configure lead visibility and transfer permissions."
              : "Add a new user to your organization. Configure their basic settings and permissions."}
          </div>
        )}
      </div>

      <Form
        layout="vertical"
        form={form}
        initialValues={{ prefix: "India", allowTransferToAdmin: false }}
        autoComplete="off"
        onFinish={(val) => {
          if (
            users?.totalElements >= subscription?.subscriptionPackage?.userLimit
          ) {
            setLimitExceed(true);
            return;
          }

          const payload = {
            ...val,
            team: val?.teams?.[0],
            teams: val?.teams || [],
            leadAccessScope,
            leadPermissions: buildLeadPermissionPayload(),
          };

          handleCreateUpdateUser(
            isTeamChange ? { ...payload, confirmTeamChange: true } : payload,
            initialValues,
            () => {
              form.resetFields();
              closeModal?.();
              setTeamChange(false);
            },
          );
        }}
        onValuesChange={(changedValues, allValues) => {
          setError();
          const keysToTrack = [
            "fullName",
            "email",
            "mobileNo",
            "prefix",
            "role",
            "teams",
            "allowTransferToAdmin",
          ];
          const initValues = {
            ...initialValues,
            prefix: formatAndExtractCountryCode(initialValues?.mobileNo)
              ?.countryLabel,
            mobileNo: formatAndExtractCountryCode(initialValues?.mobileNo)
              ?.numberWithoutCountryCode,
            fullName: initialValues?.firstName + " " + initialValues?.lastName,
            teams:
              initialValues?.teams ||
              (initialValues?.team ? [initialValues?.team] : []),
          };
          const normalize = (value) =>
            Array.isArray(value)
              ? JSON.stringify(value.slice().sort())
              : typeof value === "string"
                ? value.trim()
                : (value ?? "");

          const hasChanges = keysToTrack.some((key) => {
            return normalize(initValues[key]) !== normalize(allValues[key]);
          });

          setDisable(!hasChanges);
        }}
      >
        <div
          className={
            initialValues?.id && "tw-h-[calc(100vh-350px)] tw-overflow-y-auto"
          }
        >
          <div className="tw-mx-5 tw-my-4 rounded-2xl shadow-md tw-p-0">
            {error && <p className="text-error text-center pb-2">{error}</p>}
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={
                    <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                      Full Name
                    </span>
                  }
                  name="fullName"
                  rules={[
                    { required: true, message: "Please enter full name" },
                  ]}
                >
                  <Input
                    className="tw-modal-input"
                    placeholder="Enter full name"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={
                    <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                      Email
                    </span>
                  }
                  name="email"
                  rules={[
                    { required: true, message: "Please enter email" },
                    { type: "email", message: "Enter a valid email" },
                  ]}
                >
                  <Input
                    className="tw-modal-input"
                    placeholder="Enter email address"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={
                    <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                      Mobile Number
                    </span>
                  }
                  name="mobileNo"
                  rules={[
                    { required: true, message: "Please enter mobile number" },
                    {
                      validator: validateMobileNumber,
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter mobile number"
                    className="tw-modal-input-group mobile-no"
                    addonBefore={prefixSelector()}
                    maxLength={20}
                    onChange={(e) => handleMobileChange(e.target.value, form)}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={
                    <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                      Role
                    </span>
                  }
                  name="role"
                  rules={[{ required: true, message: "Please select a role" }]}
                >
                  <Select
                    className="tw-modal-select"
                    showSearch
                    placeholder="Search role..."
                    onSearch={fetchRoles}
                    filterOption={false}
                    notFoundContent={
                      roleFetching ? <Spin size="small" /> : null
                    }
                    allowClear
                  >
                    {roles
                      ?.filter((d) => d?.name !== userRoles?.admin)
                      .map((item) => (
                        <Option key={item.id} value={item.name}>
                          {item.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                      Team/Hotel
                    </span>
                  }
                  name="teams"
                >
                  <Select
                    className="tw-modal-select"
                    mode="multiple"
                    showSearch
                    placeholder="Search team..."
                    onSearch={fetchTeams}
                    filterOption={false}
                    notFoundContent={fetching ? <Spin size="small" /> : null}
                    allowClear
                  >
                    {teams.map((item) => (
                      <Option key={item.id} value={item.teamName}>
                        {item.teamName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {initialValues?.id ? (
                <>
                  <Col span={24}>
                    <div className="tw-font-semibold mb-8">
                      Lead Access Scope
                    </div>
                    <div className="tw-text-[#65686F] tw-mb-2 tw-text-xs">
                      Control which leads this user can view
                    </div>
                    <div className="tw-border-border-secondary tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-solid">
                      <Collapse
                        defaultActiveKey={["1"]}
                        ghost
                        bordered
                        expandIconPosition="end"
                        expandIcon={({ isActive }) =>
                          isActive ? <UpOutlined /> : <DownOutlined />
                        }
                        className="tw-user-access-collapse"
                        items={[
                          {
                            key: "1",
                            label: (
                              <div className="tw-text-[#65686F]">
                                Select Users or team...
                              </div>
                            ),
                            children: (
                              <div>
                                <div
                                  className={`tw-p-4 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-border-secondary`}
                                >
                                  <Form.Item
                                    name="leadAccessScope"
                                    initialValue="SPECIFIC_USERS"
                                    className="tw-m-0"
                                  >
                                    <Radio.Group
                                      onChange={(e) =>
                                        setLeadAccessScope(e.target.value)
                                      }
                                    >
                                      <Radio value="TEAM">Team</Radio>
                                      <br />
                                      <Radio
                                        value="SPECIFIC_USERS"
                                        className="tw-pt-2"
                                      >
                                        Specific Users
                                      </Radio>
                                    </Radio.Group>
                                  </Form.Item>
                                </div>
                              </div>
                            ),
                          },
                        ]}
                      />
                    </div>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label=""
                      name="allowTransferToAdmin"
                      valuePropName="checked"
                    >
                      <div className="tw-flex tw-items-center tw-justify-between tw-my-3">
                        <div>
                          Allow this user to transfer their own leads to admin
                        </div>

                        <Switch
                          defaultValue={!!initialValues?.allowTransferToAdmin}
                          onChange={(val) =>
                            form.setFieldsValue({ allowTransferToAdmin: val })
                          }
                        />
                      </div>
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <div className="tw-font-semibold mb-12">
                      Permissions On Other User’s Leads
                    </div>
                    <div className="tw-text-[#65686F] tw-text-xs tw-mb-2">
                      Allow this user to Edit and transfer leads
                    </div>

                    <Table
                      rowKey="id"
                      dataSource={getPermissionTableData()}
                      columns={columns}
                      pagination={false}
                      bordered
                      className="tw-mb-4"
                    />
                  </Col>
                </>
              ) : (
                <div className="tw-flex tw-text-sub-text tw-gap-2 tw-text-sm tw-bg-secondary-color tw-pl-2 tw-pr-3 tw-py-3 tw-mb-3 tw-items-center tw-rounded-[7px] tw-border-border-secondary tw-border tw-border-solid tw-mx-2">
                  <InfoCircleOutlined /> Advanced lead access settings can be
                  customized after creating the user.
                </div>
              )}

              {isTeamChange && (
                <Col span={24}>
                  <div className="fw-semibold">
                    Are you sure you want to change the team?
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-5">
                    {!!initialValues?.id && (
                      <Button
                        disabled={loading}
                        className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
                        onClick={() => {
                          closeModal();
                          setTeamChange(false);
                        }}
                      >
                        No
                      </Button>
                    )}
                    <Button
                      disabled={loading || isDisable}
                      type="primary"
                      className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
                      htmlType="submit"
                    >
                      Yes
                    </Button>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        </div>
        {!isTeamChange && (
          <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
            <Button
              disabled={loading}
              className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
              onClick={() => {
                closeModal();
                setTeamChange(false);
              }}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
              htmlType="submit"
            >
              {initialValues?.id ? "Update User" : "Create User"}
            </Button>
          </div>
        )}
      </Form>

      <Modal
        open={isLimitExceed}
        centered
        width={400}
        footer={[
          <Button
            key="ok"
            type="primary"
            className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
            onClick={() => setLimitExceed(false)}
          >
            OK
          </Button>,
        ]}
        onOk={() => setLimitExceed(false)}
        onCancel={() => setLimitExceed(false)}
      >
        You’ve already reached the user creation limit. A maximum of{" "}
        {subscription?.subscriptionPackage?.userLimit} users can be created. To
        add more users, please upgrade your plan.
      </Modal>
    </>
  );
};

export default CreateUserForm;
